<template>
  <div>
    <div
      class="loader inner"
      v-if="loading">
      <Skeleton
        v-for="i in 4"
        :key="i"
        shape="rectangle"
        width="100%"
        height="120px"
        class="tw:rounded-md" />
    </div>

    <FilterBar
      class="filter-bar"
      v-if="!loading && hasPrograms && canCreateQuoteProgram">
      <CreateQuoteProgramModal
        class="last-item"
        right
        :coverageTypes="coverageTypes"
        @needs-update="refreshData"
        @show-snackbar="showSnackbar" />

      <FileUploadModal
        title="Upload Quote Program"
        template-name="QuoteUploadTemplate-v1.xlsx"
        template-path="/downloads/QuoteUploadTemplate-v1.xlsx"
        :validate="validateQuoteProgramFile"
        :upload-action="uploadQuoteProgram">
        <template v-slot:buttonActivator="{ props: activatorProps }">
          <AppButton
            primary
            class="tw:ml-3"
            data-testid="upload-quote-btn"
            v-bind="activatorProps">
            Upload Quote Program
            <Plus
              :size="16"
              class="tw:ml-1" />
          </AppButton>
        </template>
      </FileUploadModal>
    </FilterBar>

    <div
      class="card-groups inner"
      v-if="!loading && hasPrograms">
      <!-- @vue-skip -->
      <div
        class="card-programs"
        v-for="programs of yearGroupedPrograms.values()"
        :key="programs[0].year">
        <h2>{{ programs[0].year }}</h2>
        <div class="cards">
          <div
            class="card tw:rounded-md tw:border tw:border-solid tw:border-gray-400"
            :class="{ bound: program.bound }"
            v-for="program in programs"
            :key="program.programID">
            <div class="card-header">
              <div class="card-header-title">
                <div
                  class="coverage-type text-truncate"
                  :title="program.programName">
                  {{ program.programName }}
                </div>
              </div>
              <div class="card-header-actions">
                <RouterLink
                  :to="`/account/${account.accountID}/quotes/program/${program.programID}`"
                  class="tw:btn tw:btn-outline tw:btn-sm tw:btn-secondary">
                  Open
                </RouterLink>
                <MenuDropdown
                  :items="getProgramMenuItems(program)"
                  bottom>
                  <template #action>
                    <label
                      tabindex="0"
                      class="tw:btn tw:btn-square tw:btn-ghost tw:btn-sm"
                      :data-testid="`${program.programName}-actions-menu`">
                      <EllipsisVertical :size="20" />
                    </label>
                  </template>
                </MenuDropdown>
              </div>
            </div>
            <div class="card-body">
              <div class="bullet program-type">
                <CheckCircle2
                  :size="16"
                  class="bullet-icon" />
                <span
                  class="bullet-value"
                  :data-testid="`${program.coverageType}-coverage-type`"
                  >{{ program.coverageType }} ({{ program.year }})</span
                >
              </div>
              <div class="bullet program-type">
                <LayoutList
                  :size="16"
                  class="bullet-icon" />
                <span
                  class="bullet-value"
                  :data-testid="`${program.programID}-program-type`"
                  >{{ program.programTypeName }}</span
                >
              </div>
              <div
                class="bullet expiration"
                v-if="hasExpiration(program) && isExpired(program)">
                <CalendarClock
                  :size="16"
                  class="bullet-icon tw:text-warning" />
                <span
                  class="bullet-value"
                  :data-testid="`${program.programID}-expired`"
                  >Previous program has <b class="tw:text-warning">expired</b></span
                >
              </div>
              <div
                class="bullet expiration"
                v-if="hasExpiration(program) && !isExpired(program)">
                <CalendarClock
                  :size="16"
                  class="bullet-icon" />
                <span
                  class="bullet-value"
                  :data-testid="`${program.programID}-expires`"
                  >Expires {{ relativeDateTime(program.currentProgramExpiration) }} on
                  <b>{{ filters.shorterdate(program.currentProgramExpiration) }}</b></span
                >
              </div>
              <div class="bullet inserted-by">
                <CircleUser
                  :size="16"
                  class="bullet-icon" />
                <span
                  class="bullet-value tw:text-info"
                  :data-testid="`${program.programID}-inserted-by`"
                  >{{ program.insertedBy }}</span
                >
              </div>
            </div>
            <div class="card-footer">
              <div class="stat">
                <div class="stat-label">Quotes</div>
                <div class="stat-value">14</div>
              </div>
              <div class="stat">
                <div class="stat-label">Bound</div>
                <div class="stat-value">8</div>
              </div>
              <div class="stat">
                <div class="stat-label">Premium</div>
                <div class="stat-value">$18K</div>
              </div>
              <div class="stat">
                <div class="stat-label">Limit</div>
                <div class="stat-value">$60M</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <EmptyDisplay
      :src="emptySrc"
      alt="No quote programs"
      title="No quote programs"
      text="It looks like no quote programs have been created yet. Would you like to create one now?"
      v-if="!loading && !hasPrograms">
      <CreateQuoteProgramModal
        :coverageTypes="coverageTypes"
        @needs-update="refreshData"
        @show-snackbar="showSnackbar" />
    </EmptyDisplay>
    <AppModal
      :show="deleteDialog"
      title="Delete Quote Program"
      cancel-button-label="Cancel"
      action-button-label="Delete Quote Program"
      :handle-cancel="hideDeleteDialog"
      :handle-action="deleteQuoteProgram"
      :action-disabled="deleting"
      :loading="deleting"
      size="lg">
      <template #content>
        <p>Are you sure you want to delete this program?<br />Quotes associated to this program will also be deleted.</p>
      </template>
      <template #actions>
        <AppButton
          :disabled="deleting"
          @click="hideDeleteDialog"
          data-testid="cancel-action"
          >Cancel</AppButton
        >
        <AppButton
          primary
          :disabled="deleting"
          :loading="deleting"
          @click="deleteQuoteProgram"
          data-testid="confirm-action"
          >Delete Quote Program</AppButton
        >
      </template>
    </AppModal>
  </div>
</template>

<script lang="ts">
import emptySrc from '@/assets/images/filing.svg';
import { accountService, carrierService, quoteService, referenceService, wholesalerGroupService } from '@/services/api';
import { Quote, QuoteLayer, QuoteProgram } from '@/types/Quote';
import FilterBar from '@/components/FilterBar.vue';
import CreateQuoteProgramModal from '@/components/CreateQuoteProgramModal.vue';
import { policyTypeService } from '@/services/PolicyTypeService';
import { DateTime } from 'luxon';
import { Account } from '@/types/Account';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import { ComboboxItem } from '@/types/Vuetify';
import Store from '@/store';
import { UserHasPermission } from '@/utilities/AuthUtils';
import { UserPermission } from '@/types/UserPermissions';
import FileUploadModal from '@/components/FileUploadModal.vue';
import * as XLSX from 'xlsx';
import { colLetter, convertToISODate, readWorksheet, XLSXCell, yesNoToBool } from '@/utilities/Worksheets';
import { NewBusiness, Renewal } from '@/utilities/ProgramTypes';
import { relativeDateTime } from '@/utilities/Dates';
import { filters } from '@/utilities/Filters';
import Skeleton from '@/components/v2/Skeleton.vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import { useToast } from '@/composables/useToast';
import { CircleUser, Plus, EllipsisVertical, Files, Trash2, CheckCircle2, LayoutList, CalendarClock } from 'lucide-vue-next';

export default {
  name: 'QuoteProgram',
  setup() {
    return { toast: useToast() };
  },
  components: {
    FileUploadModal,
    EmptyDisplay,
    FilterBar,
    CreateQuoteProgramModal,
    Skeleton,
    AppModal,
    AppButton,
    MenuDropdown,
    CircleUser,
    Plus,
    EllipsisVertical,
    CheckCircle2,
    LayoutList,
    CalendarClock,
  },
  data: () => ({
    filters,
    emptySrc,
    account: {} as Account,
    quotePrograms: [] as QuoteProgram[],
    loading: true,
    coverageTypes: [] as ComboboxItem[],
    deleteDialog: false,
    deleteProgram: null as QuoteProgram | null,
    deleting: false,
    upload: {
      quoteProgram: undefined as QuoteProgram | undefined,
      quoteLayers: [] as QuoteLayer[],
      quotes: [] as Quote[],
    },
  }),
  computed: {
    hasPrograms(): boolean {
      return !this.noPrograms;
    },
    noPrograms(): boolean {
      return this.yearGroupedPrograms === null || this.yearGroupedPrograms.size === 0;
    },
    yearGroupedPrograms(): Map<number, QuoteProgram[]> | null {
      if (this.quotePrograms) {
        return this.quotePrograms.reduce((acc, curr) => {
          if (!acc.has(curr.year)) {
            acc.set(curr.year, []);
          }
          acc.get(curr.year)?.push(curr);
          return acc;
        }, new Map<number, QuoteProgram[]>());
      }
      return null;
    },
    canCreateQuoteProgram(): boolean {
      return UserHasPermission(Store.state.user, UserPermission.QuotesWrite);
    },
  },
  methods: {
    relativeDateTime,
    hideDeleteDialog() {
      this.deleteProgram = null;
      this.deleteDialog = false;
    },
    showDeleteDialog(program: QuoteProgram) {
      this.deleteProgram = program;
      this.deleteDialog = true;
    },
    isExpired(program: QuoteProgram): boolean {
      return program.currentProgramExpiration ? DateTime.fromISO(program.currentProgramExpiration) <= DateTime.local() : false;
    },
    hasExpiration(program: QuoteProgram): boolean {
      return !!program.currentProgramExpiration;
    },
    async deleteQuoteProgram() {
      if (this.deleteProgram) {
        this.deleting = true;
        await quoteService.DeleteQuoteProgram(this.$route.params.id as string, '' + this.deleteProgram?.programID);
        this.quotePrograms = this.quotePrograms.filter((p) => p.programID !== this.deleteProgram?.programID);
        this.deleteProgram = null;
        this.deleteDialog = false;
        this.deleting = false;
      }
    },
    async refreshData(): Promise<void> {
      this.loading = true;
      const [account, quotePrograms, coverageTypes] = await Promise.all([
        accountService.findById(this.$route.params.id as string),
        quoteService.GetQuotePrograms(this.$route.params.id as string),
        policyTypeService.GetDistinctCoverageTypes(),
      ]);
      this.account = account;
      this.quotePrograms = quotePrograms;
      this.coverageTypes = coverageTypes
        .map((t) => ({ title: t.coverageType, value: t.coverageTypeId }))
        .sort((a, b) => a.title.localeCompare(b.title));
      this.loading = false;
    },
    showSnackbar(): void {
      this.toast.success('Quote Program Created');
    },
    getProgramMenuItems(program: QuoteProgram): MenuItem[] {
      return [
        {
          label: 'Open',
          handler: () => this.$router.push(`/account/${this.account.accountID}/quotes/program/${program.programID}`),
          icon: Files,
        },
        {
          label: 'Delete Program',
          handler: () => this.showDeleteDialog(program),
          icon: Trash2,
          iconColorClass: 'tw:text-error',
        },
      ];
    },
    validateQuoteProgramFile(file: File | undefined): string[] | undefined {
      if (!file) return ['The file could not be opened'];

      const errors = [] as string[];

      let reader = new FileReader();
      reader.onload = () => {
        try {
          interface Cell<T> {
            row: number;
            col: number;
            prop: string;
            setVal: (p: T, val: XLSXCell) => void;
          }

          interface Layer extends QuoteLayer {
            row: number;
            col: number;
          }

          const programCells = [
            {
              row: 2,
              col: 1,
              prop: 'Program Name',
              setVal: (p, val) => {
                p.programName = (val.data.w || '').trim();
              },
            },
            {
              row: 6,
              col: 1,
              prop: 'Program Type',
              setVal: (p, val) => {
                p.programTypeName = (val.data.w || '').trim();
              },
            },
            {
              row: 6,
              col: 2,
              prop: 'Renewal Date',
              setVal: (p, val) => {
                p.currentProgramExpiration = convertToISODate(val.data.w);
              },
            },
            {
              row: 10,
              col: 1,
              prop: 'Coverage Type',
              setVal: (p, val) => {
                p.coverageType = (val.data.w || '').trim();
              },
            },
            {
              row: 10,
              col: 2,
              prop: 'Program Year',
              setVal: (p, val) => {
                p.year = val.data.v || -1;
              },
            },
          ] as Cell<QuoteProgram>[];

          const layerCells = [
            {
              row: -1,
              col: 4,
              prop: 'Layer Type',
              setVal: (p, val) => {
                p.layerType = (val.data.w || '').trim();
              },
            },
            {
              row: -1,
              col: 5,
              prop: 'Layer Name',
              setVal: (p, val) => {
                p.layerName = (val.data.w || '').trim();
              },
            },
            {
              row: -1,
              col: 6,
              prop: 'Layer ID',
              setVal: (p, val) => {
                p.layerIdStr = (val.data.w || '').trim();
              },
            },
          ] as Cell<Layer>[];

          // find program and layer info in the sheet

          const pgm = {} as QuoteProgram;
          const layerMap = new Map<number, Layer>();

          readWorksheet(XLSX.read(reader.result, { type: 'array' }), 0).forEach((cell) => {
            const [row, col] = [cell.address.r, cell.address.c];

            // program info
            const isProgramCell = !!programCells.find((c) => {
              if (row === c.row && col === c.col) {
                c.setVal(pgm, cell);
                return true;
              }
            });
            if (isProgramCell) return;

            // layer info
            if (row >= 1 && row <= 22 && col >= 4 && col <= 6) {
              if (!layerMap.has(row)) layerMap.set(row, { row, col, layerName: '', layerType: '' } as Layer);

              let quoteLayer = layerMap.get(row) || ({} as Layer);
              layerCells.find((c) => {
                if (col === c.col) {
                  c.setVal(quoteLayer, cell);
                  return true;
                }
              });
            }
          });

          // validate

          pgm.accountID = parseInt(this.$route.params.id as string);

          if (!pgm.programName || pgm.programName === '') errors.push('Program Name is required');
          if (!pgm.programTypeName || pgm.programTypeName === '') errors.push('Program Type is required');
          if (!pgm.year || pgm.year === -1) errors.push('Program Year is required');

          if (!pgm.coverageType || pgm.coverageType === '') {
            errors.push('Coverage Type is required');
          } else {
            // find coverageTypeId from ref data
            const coverageType = this.coverageTypes.find((ct) => ct.title === pgm.coverageType);
            if (!coverageType) {
              errors.push('Invalid Coverage Type was selected');
            } else {
              pgm.coverageTypeId = coverageType.value;
            }
          }

          switch (pgm.programTypeName) {
            case 'Renewal':
              if (pgm.currentProgramExpiration === '' || !pgm.currentProgramExpiration) {
                errors.push('Renewal Date is required');
              }
              pgm.programType = Renewal;
              break;
            case 'New Business':
              pgm.programType = NewBusiness;
              break;
            default:
              errors.push('Invalid Program Type was selected');
          }
          this.upload.quoteProgram = pgm;

          // sort by row num asc -> get just the layer objects -> discard rid of empty layers
          const layers = Array.from(layerMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map((l) => l[1])
            .filter((l) => [l.layerName, l.layerType].join('') !== '');

          layers.forEach((la) => {
            const cellLocation = `Cell ${colLetter(la.col)}${la.row}`;
            if (!la.layerType || la.layerType === '') errors.push(`${cellLocation}: Layer Type is required`);
            if (!la.layerName || la.layerName === '') errors.push(`${cellLocation}: Layer Name is required`);
          });
          this.upload.quoteLayers = layers;

          // process Quotes sheet

          interface quote extends Quote {
            row: number;
            col: number;
            layerShortId: string;
          }

          const quoteCells = [
            {
              row: -1,
              col: 0,
              prop: 'Layer ID',
              setVal: (q, val) => {
                q.layerIdStr = val.data.w?.trim();
              },
            },
            {
              row: -1,
              col: 1,
              prop: 'Carrier',
              setVal: (q, val) => {
                q.insurerGroup = val.data.w?.trim();
              },
            },
            {
              row: -1,
              col: 2,
              prop: 'Wholesaler / MGA',
              setVal: (q, val) => {
                q.wholesalerGroup = val.data.w?.trim();
              },
            },
            {
              row: -1,
              col: 3,
              prop: 'Response Date',
              setVal: (q, val) => {
                q.responseDate = convertToISODate(val.data.w);
              },
            },
            {
              row: -1,
              col: 4,
              prop: 'Response Status',
              setVal: (q, val) => {
                q.responseStatus = val.data.w?.trim();
              },
            },
            {
              row: -1,
              col: 5,
              prop: 'Admitted',
              setVal: (q, val) => {
                q.admitted = yesNoToBool(val.data.w);
              },
            },
            {
              row: -1,
              col: 6,
              prop: 'Incumbent',
              setVal: (q, val) => {
                q.incumbent = yesNoToBool(val.data.w);
              },
            },
            {
              row: -1,
              col: 7,
              prop: 'Bound',
              setVal: (q, val) => {
                q.bound = yesNoToBool(val.data.w);
              },
            },
            {
              row: -1,
              col: 8,
              prop: 'Reason Declined',
              setVal: (q, val) => {
                q.declinedReason = (val.data.w || '').trim();
              },
            },
            {
              row: -1,
              col: 9,
              prop: 'Aggregate Limit',
              setVal: (q, val) => {
                q.aggregateLimit = val.data.v;
              },
            },
            {
              row: -1,
              col: 10,
              prop: 'Quotashare Total',
              setVal: (q, val) => {
                q.quotashareTotal = val.data.v;
              },
            },
            {
              row: -1,
              col: 11,
              prop: 'Retention',
              setVal: (q, val) => {
                q.retention = val.data.v;
              },
            },
            {
              row: -1,
              col: 12,
              prop: 'Attachment Point',
              setVal: (q, val) => {
                q.attachmentPoint = val.data.v;
              },
            },
            {
              row: -1,
              col: 13,
              prop: 'Premium',
              setVal: (q, val) => {
                q.premium = val.data.v;
              },
            },
            {
              row: -1,
              col: 14,
              prop: 'Commission',
              setVal: (q, val) => {
                q.commissionPercent = val.data.v;
              },
            },
            {
              row: -1,
              col: 15,
              prop: 'Surplus Lines Tax',
              setVal: (q, val) => {
                q.surplusLinesTax = val.data.v;
              },
            },
          ] as Cell<quote>[];

          const quoteMap = new Map<number, quote>();

          readWorksheet(XLSX.read(reader.result, { type: 'array' }), 1).forEach((cell) => {
            const [row, col] = [cell.address.r, cell.address.c];

            if (row >= 1 && col >= 0 && col <= 15) {
              if (!quoteMap.has(row)) quoteMap.set(row, { row, col } as quote);

              let quoteLayer = quoteMap.get(row) || ({} as quote);
              quoteCells.find((c) => {
                if (col === c.col) {
                  c.setVal(quoteLayer, cell);
                  return true;
                }
              });
            }
          });

          // validate quotes

          const quotes = Array.from(quoteMap.entries())
            .sort((q1, q2) => q1[0] - q2[0])
            .map((q) => q[1]);
          quotes.forEach((q) => {
            const cellLocation = `Quotes Cell ${colLetter(q.col)}${q.row}`;
            if (!q.layerIdStr || q.layerIdStr === '') errors.push(`${cellLocation}: Layer ID is required`);
            if ([q.wholesalerGroup, q.insurerGroup].join('') === '') errors.push(`${cellLocation}: Wholesaler or carrier is required`);
            if (q.responseStatus === 'Declined' && q.declinedReason === '')
              errors.push(`${cellLocation}: Reason Declined required for declined quotes`);
          });
          this.upload.quotes = quotes;
        } catch (error) {
          console.error(error);
          errors.push('The selected file could not be opened');
        }
        return;
      };
      reader.readAsArrayBuffer(file);
      return errors;
    },
    async uploadQuoteProgram(): Promise<string[] | undefined> {
      // This needs to be cleaned up... the validate methods actually parses the file and builds the program
      // and this function uploads that built object. The correct way would probably be to create a function prop that
      // parses the file (passed to the FileUploadModal), which then passes the result to the validate function, and if
      // no errors, then pass that to the upload function.

      if (!this.upload.quoteProgram) {
        return;
      }

      const { quoteProgram, quoteLayers, quotes } = this.upload;

      const [wholesalers, carriers, quoteResponseTypes, declineReasonTypes] = await Promise.all([
        wholesalerGroupService.list(true),
        carrierService.list(),
        referenceService.quoteResponseTypes(),
        referenceService.quoteDeclineReasonTypes(),
      ]);

      // match up IDs to string literals from spreadsheet before sending to API
      quotes.forEach((q) => {
        const carrierId = carriers.find((c) => c.name === q.insurerGroup)?.ID;
        q.insurerGroupId = carrierId ? carrierId : undefined;

        q.wholesalerGroupId = wholesalers.find((w) => w.name === q.wholesalerGroup)?.id;
        q.declinedReasonId = declineReasonTypes.find((t) => t.name === q.declinedReason)?.id;
        q.responseStatusId = quoteResponseTypes.find((t) => t.name === q.responseStatus)?.id;
      });

      await quoteService.CreateFullQuoteProgram(quoteProgram, quoteLayers, quotes);
      await this.refreshData();

      this.upload = {
        quoteProgram: undefined,
        quoteLayers: [],
        quotes: [],
      };
    },
  },
  created(): Promise<void> {
    return this.refreshData();
  },
};
</script>

<style scoped>
.card-groups {
  display: grid;
  grid-template-columns: 1fr;
}

.card-programs {
  margin: 2rem 0 1rem;
}

.card-programs:first-child {
  margin: 0;
}

.card-programs > h2 {
  font-size: 1.8rem;
  border-bottom: 1px solid #d1d5db;
  margin-bottom: 1rem;
}

.cards,
.loader {
  display: grid;
  grid-gap: 2rem;
  grid-row-gap: 2rem;
  padding-bottom: 1rem;
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 1150px) {
  .cards,
  .loader {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .cards,
  .loader {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .cards,
  .loader {
    grid-template-columns: 1fr;
  }
}

.card {
  display: grid;
  grid-template-rows: repeat(3, auto);
}

.card:hover {
  box-shadow: 2px 2px 2px #d1d5db;
}

.card > .card-header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 0.5rem;
  padding: 1rem 1rem 0 1rem;
}

.card > .card-header > .card-header-title {
  display: grid;
  grid-template-columns: auto auto 1fr;
  grid-column-gap: 0.25rem;
  align-items: baseline;
  align-content: baseline;
}

.card > .card-header > .card-header-title > .year,
.card > .card-header > .card-header-title > .coverage-type {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.1;
}

.card > .card-header > .card-header-title > .year {
  color: #9ca3af;
  font-size: 0.9rem;
}

.card > .card-header > .card-header-actions {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 0.25rem;
  align-items: center;
  align-content: center;
}

.card > .card-body {
  padding: 1rem 1rem 1.5rem 1rem;
}

.card > .card-body > .bullet {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.25rem;
  align-content: center;
  align-items: center;
}

.card > .card-body > .bullet > .bullet-value {
  font-size: 0.8rem;
  opacity: 0.7;
}

.card > .card-footer {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-top: 1px solid #d1d5db;
  border-radius: 0 0 4px 4px;
  display: none;
  grid-template-columns: repeat(4, auto);
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;
}

.card > .card-footer > .stat {
  text-align: center;
}

.card > .card-footer > .stat > .stat-label {
  font-size: 0.6rem;
  text-transform: uppercase;
}

.card > .card-footer > .stat > .stat-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.bound {
  border: 1px solid var(--color-primary);
}

.filter-bar {
  display: flex;
}

.last-item {
  justify-self: end;
  background-color: #ffffff;
}
</style>
